import type { NwsQuantity } from './types'

// NWS observations are SI. These convert to the US units the UI shows.
export const cToF = (c: number) => (c * 9) / 5 + 32
export const msToMph = (ms: number) => ms * 2.236936
export const kmhToMph = (kmh: number) => kmh * 0.621371
export const paToInHg = (pa: number) => pa * 0.0002953
export const mToMi = (m: number) => m / 1609.344

/** Round, tolerating null. Returns '—' for missing values. */
export function show(value: number | null | undefined, digits = 0, suffix = ''): string {
  if (value == null || Number.isNaN(value)) return '—'
  return `${value.toFixed(digits)}${suffix}`
}

/** Temperature in °F from an NWS SI quantity (°C). */
export function tempF(q: NwsQuantity | null | undefined): number | null {
  if (!q || q.value == null) return null
  return cToF(q.value)
}

/** Wind speed in mph from an NWS quantity (unitCode may be m_s-1 or km_h-1). */
export function windMph(q: NwsQuantity | null | undefined): number | null {
  if (!q || q.value == null) return null
  return q.unitCode?.includes('km_h') ? kmhToMph(q.value) : msToMph(q.value)
}

/** Barometric pressure in inHg from an NWS quantity (Pa, or already-hPa). */
export function pressureInHg(q: NwsQuantity | null | undefined): number | null {
  if (!q || q.value == null) return null
  // NWS reports Pa (unitCode wmoUnit:Pa). Guard against feeds already in hPa.
  const pa = q.value > 2000 ? q.value : q.value * 100
  return paToInHg(pa)
}

/** 16-point compass label from degrees. */
export function compass(deg: number | null | undefined): string {
  if (deg == null) return ''
  const dirs = [
    'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
  ]
  return dirs[Math.round(deg / 22.5) % 16]
}

/** Parse NWS forecast windSpeed strings like "10 mph" or "5 to 10 mph" → upper number. */
export function parseWindMph(s: string): number | null {
  const nums = s.match(/\d+/g)
  if (!nums) return null
  return Number(nums[nums.length - 1])
}
